import React, { useEffect, useContext, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ProfileContext } from '../../../context/Profile.context';
import { errors, sectionTitles } from '../../../constants';
import { createID, isFutureDate } from '../../../utils/helper';
import { Trash2 } from 'lucide-react';
import { Box, Button, DateField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InfoPanel, Locale, Table, Typography, TextField, SectionTitle } from '@otcmarketsgroup/otcm-ui-lib';
import { testIds } from '../../../constants/testIds';

dayjs.extend(utc);
dayjs.extend(timezone);

const currentEST = dayjs().tz('America/New_York');

const FROM_NAME = 'fromName';
const TO_NAME = 'toName';
const EFFECTIVE_DATE = 'date';
const PREEXISTING = 'isPreExisting';

const defaultHistory = {
    [FROM_NAME]: null,
    [TO_NAME]: null,
    [EFFECTIVE_DATE]: null
};

const buildDescription = item =>
    `Company name changed from ${item[FROM_NAME]} to ${item[TO_NAME]}`;

const nameHistTestIds = testIds.companyInfo.nameHistory;

const CompanyNameHistory = () => {
    const [profileState, dispatchProfile] = useContext(ProfileContext);
    const [viewFormDialog, setFormDialog] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [history, setHistory] = useState(defaultHistory);
    const [validationError, setValidationError] = useState(null);

    const isEditing = Boolean(history.id);
    const profileData = profileState.profile;
    const isReadOnly = profileState.readOnly;
    const companyNameHistoryList = profileData.companyInfo.companyNameHistoryList?.map(item => ({
        ...item,
        description: buildDescription(item),
    }));

    useEffect(() => {
        if (!viewFormDialog) {
            setHistory(defaultHistory);
            setValidationError(null);
        }
    }, [viewFormDialog]);

    const formDialogClose = () => {
        setFormDialog(false);
    };

    const onValueChanged = (value, field) => {
        setHistory(prev => ({ ...prev, [field]: value }));
    };

    const handleRowClick = (e, item) => {
        if (item[PREEXISTING]) return;

        setHistory(item);
        setFormDialog(true);
    };

    const handleAdd = () => {
        setValidationError(null);

        if (!validateForm()) return;

        const updatedCompanyInfo = { ...profileData.companyInfo };
        if (!updatedCompanyInfo.companyNameHistoryList) updatedCompanyInfo.companyNameHistoryList = [];

        updatedCompanyInfo.companyNameHistoryList = [
            ...updatedCompanyInfo.companyNameHistoryList,
            {
                ...history,
                id: createID(updatedCompanyInfo.companyNameHistoryList),
                description: buildDescription(history),
            },
        ];

        dispatchProfile({ type: 'UPDATE_PROFILE', field: 'companyInfo', payload: updatedCompanyInfo });
        formDialogClose();
    };

    const handleUpdate = () => {
        setValidationError(null);
        if (!validateForm()) return;

        const updatedCompanyInfo = {
            ...profileData.companyInfo,
            companyNameHistoryList: profileData.companyInfo.companyNameHistoryList.map(item =>
                item.id === history.id ? { ...history, description: buildDescription(history) } : item
            ),
        };

        dispatchProfile({ type: 'UPDATE_PROFILE', field: 'companyInfo', payload: updatedCompanyInfo });
        formDialogClose();
    };

    const validateForm = () => {
        if (!history[FROM_NAME] || !history[TO_NAME] || !history[EFFECTIVE_DATE] || isFutureDate(history[EFFECTIVE_DATE])) {
            setValidationError(true);
            return false;
        }

        return true;
    };

    const handleRemoveRow = (e, item) => {
        e.stopPropagation();

        setOpenDialog(item);
    };

    const confirmRemoveRow = () => {
        const updatedObj = { ...profileData.companyInfo };
        updatedObj.companyNameHistoryList = updatedObj.companyNameHistoryList.filter(obj => obj.id !== openDialog.id);

        dispatchProfile({
            type: 'UPDATE_PROFILE',
            field: 'companyInfo',
            payload: updatedObj
        });
        setOpenDialog(false);
    };

    const columns = [
        {
            header: 'Description',
            id: 'description'
        },
        {
            header: 'Effective Date',
            id: 'date',
            renderCell: item => <Locale type='date'>{item.date}</Locale>
        },
        {
            cellProps: {
                align: 'right'
            },
            width: '75px',
            renderCell: item => item[PREEXISTING] ? null : <IconButton color='primary' data-testid={nameHistTestIds.deleteBtn(item.id)} onClick={e => handleRemoveRow(e, item)}><Trash2 size={20} /></IconButton>
        }
    ];

    return <Box>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box display='flex' alignItems='center' gap={1}>
                <SectionTitle noUnderline>{sectionTitles.COMPANY_NAME_HISTORY}</SectionTitle>
            </Box>
            {!isReadOnly && <Button variant='text' data-testid={nameHistTestIds.addBtn} onClick={() => setFormDialog(true)}>
                Add Company Name History
            </Button>}
        </Box>
        <Box data-testid={nameHistTestIds.table}>
            <Table columns={columns} data={companyNameHistoryList} handleRowClick={!isReadOnly ? (e, item) => handleRowClick(e, item) : undefined} />
        </Box>

        <Dialog open={viewFormDialog} onClose={formDialogClose} data-testid={nameHistTestIds.dialog}>
            <DialogTitle title='Company Name History' onClose={formDialogClose} />
            <DialogContent>
                <Typography variant='body1' component='p' gutterBottom>
                    Company name changed from
                </Typography>
                <Box display='grid' gap={2} gridTemplateColumns={{ desktop: 'repeat(6, 1fr)', mobile: '1fr' }}>
                    <Box gridColumn='span 6' data-testid={nameHistTestIds.previousName}>
                        <TextField
                            label='Previous Name'
                            placeholder='Enter Previous Name'
                            maxLength={75}
                            isRequired
                            showClearButton
                            placeholder='Enter text here'
                            value={history[FROM_NAME]}
                            error={validationError && !history[FROM_NAME]}
                            helperText={validationError && !history[FROM_NAME] ? 'Required' : undefined}
                            onChange={e => onValueChanged(e.target.value, FROM_NAME)}
                        />
                    </Box>
                    <Typography variant='body1' component='p' gutterBottom>
                        to
                    </Typography>
                    <Box gridColumn='span 6' data-testid={nameHistTestIds.newName}>
                        <TextField
                            label='New Name'
                            placeholder='Enter New Name'
                            maxLength={75}
                            isRequired
                            showClearButton
                            placeholder='Enter text here'
                            value={history[TO_NAME]}
                            error={validationError && !history[TO_NAME]}
                            helperText={validationError && !history[TO_NAME] ? 'Required' : undefined}
                            onChange={e => onValueChanged(e.target.value, TO_NAME)}
                        />
                    </Box>
                    <Box gridColumn='span 3' data-testid={nameHistTestIds.effectiveDate}>
                        <DateField
                            id='EFFECTIVE_DATE'
                            type="date"
                            label="Effective Date"
                            maxDate={currentEST}
                            showClearButton
                            value={history[EFFECTIVE_DATE]}
                            error={validationError && (!history[EFFECTIVE_DATE] || isFutureDate(history[EFFECTIVE_DATE]))}
                            helperText={
                                validationError
                                    ? !history[EFFECTIVE_DATE]
                                        ? 'Required'
                                        : isFutureDate(history[EFFECTIVE_DATE])
                                            ? 'Cannot be future date'
                                            : undefined
                                    : undefined
                            }
                            onSelectDate={date => onValueChanged(date, EFFECTIVE_DATE)}
                        />
                    </Box>
                </Box>
                {validationError && <Box mt={2}>
                    <InfoPanel title={errors.INCOMPLETE_DATA} isWarning />
                </Box>}
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' data-testid={nameHistTestIds.dialogCancelBtn} onClick={formDialogClose}>Cancel</Button>
                <Button data-testid={nameHistTestIds.dialogSubmitBtn} onClick={isEditing ? handleUpdate : handleAdd}>{isEditing ? 'Update' : 'Add'}</Button>
            </DialogActions>
        </Dialog>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} data-testid={nameHistTestIds.removeDialog}>
            <DialogTitle title='Remove Company Name History' onClose={() => setOpenDialog(false)} />
            <DialogContent>
                <Typography variant='body1' component='p' gutterBottom>
                    Are you sure you want to delete?
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' color='warning' data-testid={nameHistTestIds.removeCancelBtn} onClick={() => setOpenDialog(false)}>
                    Cancel
                </Button>
                <Button color='warning' data-testid={nameHistTestIds.removeConfirmBtn} onClick={confirmRemoveRow}>
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
    </Box>;
};

CompanyNameHistory.propTypes = {

};

export default CompanyNameHistory;
